import React, { useState } from 'react';
import { Collapse, ConfigProvider, Tag, Skeleton } from 'antd';
import { Loader2, Package, DollarSign, FileText, Settings, User } from 'lucide-react';

import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { SelectField } from '@/components/form/SelectField';
import { TextareaField } from '@/components/form/TextareaField';
import { FormattedTextField } from '@/components/form/FormattedTextField';
import { FileUploadField } from '@/components/form/FileUploadField';
import { PipelineInfoRow } from '@/components/form/pipelineFieldRow';
import { FieldConfig } from '@/config/formConfig';
import { useInlineContratoForm } from '../../hooks/useInlineContratoForm';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Package,
  DollarSign,
  FileText,
  Settings,
  User,
};

interface ContratoInlineFormProps {
  clienteId: string;
  /** Contrato já existente para este cliente — presença ativa o modo edição */
  contratoId?: string;
  onSaved?: () => void;
  /** Somente visualização: campos desabilitados e sem botão de salvar */
  readOnly?: boolean;
}

function formatCurrencyDisplay(value: unknown) {
  const n = Number(value);
  if (!n || Number.isNaN(n)) return 'R$ 0,00';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function ContratoInlineForm({
  clienteId,
  contratoId,
  onSaved,
  readOnly = false,
}: ContratoInlineFormProps) {
  const logic = useInlineContratoForm({ clienteId, contratoId, onSaved });
  const { form, sections, sectionFields, fieldToSectionMap, grupos, bens, consultores } = logic;

  const [openPanels, setOpenPanels] = useState<string[]>(
    sections[0] ? [sections[0].id] : []
  );

  if (logic.loadingData || logic.isLoadingContrato) {
    return (
      <div className="px-3 py-2 space-y-2">
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  const getSelectOptions = (field: FieldConfig) => {
    if (field.options) return field.options;

    switch (field.id) {
      case 'grupo_id': {
        if (!grupos) return [];
        return grupos
          .filter((grupo: any) => grupo && grupo.id && grupo.ativo !== false)
          .map((grupo: any) => {
            const taxaMedia = logic.calcularTaxaMediaGrupo(grupo);
            const taxaText = taxaMedia ? ` (Taxa: ${taxaMedia}%)` : '';
            const tituloText = grupo.titulo ? ` - ${grupo.titulo}` : '';
            return {
              value: grupo.id.toString(),
              label: `Grupo ${grupo.numero_grupo} - ${grupo.especie}${tituloText}${taxaText}`,
            };
          });
      }
      case 'bem_id': {
        if (!bens) return [];
        const grupoSelecionado = form.watch('grupo_id');
        if (!grupoSelecionado) return [];
        return bens
          .filter((bem: any) => bem && bem.id && bem.codigo_bem && bem.grupo_id?.toString() === grupoSelecionado)
          .map((bem: any) => {
            const modalidade = bem.tipo_pagamento === 'PAGAMENTO INCORPORADO' ? 'Incorporado' : 'Até Contemplação';
            const preco = (bem.preco_bem || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
            return {
              value: bem.id.toString(),
              label: `${bem.tipo_bem} #${bem.codigo_bem} (${modalidade}) - ${preco}`,
            };
          });
      }
      case 'consultor_id': {
        if (!consultores) return [];
        return consultores
          .filter((c: any) => c && c.id && c.ativo !== false)
          .map((c: any) => ({ value: c.id.toString(), label: c.nome }));
      }
      default:
        return [];
    }
  };

  const renderField = (field: FieldConfig) => {
    if (field.type === 'file') {
      return (
        <FileUploadField
          key={field.id}
          name={field.id}
          label={field.label}
          required={field.required}
          value={form.watch(field.id) as unknown as FileList | undefined}
          onChange={(files) => form.setValue(field.id, files as any)}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          disabled={readOnly}
        />
      );
    }

    if (field.validation?.custom === 'readonly') {
      const rawValue = form.watch(field.id);
      const displayValue =
        field.id === 'taxa_adm'
          ? `${parseFloat(String(rawValue || '0')) || 0}%`
          : formatCurrencyDisplay(rawValue);
      return <PipelineInfoRow key={field.id} label={field.label} value={displayValue} />;
    }

    if (field.type === 'select') {
      return (
        <SelectField
          key={field.id}
          form={form}
          name={field.id}
          label={field.label}
          required={field.required}
          placeholder={field.placeholder}
          options={getSelectOptions(field)}
          showSearch
        />
      );
    }

    if (field.type === 'textarea') {
      return (
        <TextareaField
          key={field.id}
          form={form}
          name={field.id}
          label={field.label}
          required={field.required}
          placeholder={field.placeholder}
        />
      );
    }

    if (field.validation?.custom === 'currency_validation') {
      return (
        <FormattedTextField
          key={field.id}
          form={form}
          name={field.id}
          label={field.label}
          required={field.required}
          placeholder={field.placeholder}
          formatter="moeda"
        />
      );
    }

    return (
      <FormattedTextField
        key={field.id}
        form={form}
        name={field.id}
        label={field.label}
        required={field.required}
        placeholder={field.placeholder}
        formatter="texto"
      />
    );
  };

  const getSectionStatus = (sectionId: string): 'empty' | 'partial' | 'complete' | 'error' => {
    const fields = sectionFields[sectionId] || [];
    const errors = form.formState.errors;
    const values = form.getValues();

    if (fields.some((field) => field.id in errors)) return 'error';

    const trackable = fields.filter(
      (field) => field.validation?.custom !== 'readonly' && field.type !== 'file'
    );
    if (trackable.length === 0) return 'empty';

    const filled = trackable.filter((field) => {
      const val = values[field.id as keyof typeof values];
      if (typeof val === 'number') return val > 0;
      return val !== undefined && val !== null && val !== '';
    });

    if (filled.length === 0) return 'empty';
    if (filled.length === trackable.length) return 'complete';
    return 'partial';
  };

  const handleSubmitClick = async () => {
    const isValid = await form.trigger();

    if (!isValid) {
      const errors = form.formState.errors;
      const firstErrorField = Object.keys(errors)[0];
      const targetSection = fieldToSectionMap[firstErrorField] || sections[0]?.id;

      if (targetSection) {
        setOpenPanels([targetSection]);
        setTimeout(() => {
          const el =
            document.querySelector(`[name="${firstErrorField}"]`) ||
            document.querySelector(`[id="${firstErrorField}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            (el as HTMLElement).focus();
          }
        }, 300);
      }
      return;
    }

    form.handleSubmit(logic.handleSubmit)();
  };

  return (
    <Form {...form}>
      <div className="space-y-2">
        <Collapse
          activeKey={openPanels}
          onChange={(keys) => setOpenPanels(keys as string[])}
          className="rounded-md overflow-hidden border border-[#eceef2] dark:border-zinc-800"
          items={sections.map((section) => {
            const status = getSectionStatus(section.id);
            const Icon = iconMap[section.icon] || Package;
            return {
              key: section.id,
              label: (
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5 text-[#8f9aab]" />
                    <span className="text-[13px] font-medium text-gray-800 dark:text-gray-200">
                      {section.title}
                    </span>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    {status === 'complete' && (
                      <Tag color="green" style={{ fontSize: '11px' }}>Preenchido</Tag>
                    )}
                    {status === 'partial' && (
                      <Tag color="orange" style={{ fontSize: '11px' }}>Parcial</Tag>
                    )}
                    {status === 'error' && (
                      <Tag color="red" style={{ fontSize: '11px' }}>Erro</Tag>
                    )}
                    {status === 'empty' && (
                      <Tag style={{ fontSize: '11px' }}>Vazio</Tag>
                    )}
                  </div>
                </div>
              ),
              children: (
                <ConfigProvider componentDisabled={readOnly}>
                  <div className="flex flex-col">
                    {(sectionFields[section.id] || []).map((field) => renderField(field))}
                  </div>
                </ConfigProvider>
              ),
            };
          })}
        />

        {!readOnly && (
          <div className="flex items-center gap-2 px-2 pt-2 pb-5">
            <Button
              type="button"
              size="sm"
              onClick={handleSubmitClick}
              disabled={logic.isSubmitting}
              className="bg-[#FF3737] hover:bg-[#D92B2B] flex items-center gap-1.5 flex-1"
            >
              {logic.isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {logic.isEditing ? 'Salvar alterações' : 'Criar contrato'}
            </Button>
          </div>
        )}
      </div>
    </Form>
  );
}

export default ContratoInlineForm;
